/**
 * @file lorryDayOrder.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 * @description The order a lorry's orders are listed in, with lorry day
 *              planning on (Cam, 2026-10-15):
 *                - "in day view, the orders should be shown with the first
 *                  drop top of the list, last drop bottom" — DROP ORDER: the
 *                  drop time, then the drop order of drops sharing a time
 *                  (drop_seq), then the collection time, then where the row
 *                  already is; a row with no drop time goes after the timed
 *                  ones (by its collection time).
 *                - "Changing times in the main board should NOT reorder the
 *                  rows automatically, just show a '!'": the board keeps its
 *                  own order (the saved sequence); a row that drops before
 *                  one above it is marked (outOfOrderIds) until the planner
 *                  presses "Re-order by time" (reorderGroup), which sorts that
 *                  lorry's rows and leaves every other row where it is.
 *              Pure: no React, no fetch.
 */

import { minutesOf, normaliseLinePlan } from './lorryDay';

const LATE = 24 * 60 + 1;

/** [drop minute, drop seq, collect minute] for a board row (its line_plan, scheduled_time). */
export function boardRowKey(row) {
  const lp = normaliseLinePlan(row && row.line_plan);
  return keyOf(lp.dropTime, lp.dropSeq, row && row.scheduled_time);
}

/** The same key for one of a lorry day's rows (lorryDay.buildLorryDay's day.rows). */
export function dayRowKey(r) {
  return keyOf(r && r.dropTime, r && r.dropSeq, r && r.collectTime);
}

function keyOf(dropTime, dropSeq, collectTime) {
  const d = minutesOf(dropTime);
  const c = minutesOf(collectTime);
  return [d == null ? LATE : d, Number.isInteger(dropSeq) ? dropSeq : Infinity, c == null ? LATE : c];
}

/** Compare two keys: negative when `a` comes first. */
export function compareKeys(a, b) {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/** `rows` in drop order; ties keep the order they came in. `keyFn` defaults to boardRowKey. */
export function sortByDropOrder(rows, keyFn = boardRowKey) {
  return (rows || [])
    .map((r, i) => ({ r, i, k: keyFn(r) }))
    .sort((a, b) => compareKeys(a.k, b.k) || (a.i - b.i))
    .map(x => x.r);
}

/**
 * The rows (by `idOf`) that come before a row above them in drop order —
 * the ones a "!" marks. A row with the same key as one above is in order.
 */
export function outOfOrderIds(rows, { keyFn = boardRowKey, idOf = (r) => r.order_line_id } = {}) {
  const out = new Set();
  let highest = null;
  for (const r of rows || []) {
    const k = keyFn(r);
    if (highest && compareKeys(k, highest) < 0) out.add(idOf(r));
    else highest = k;
  }
  return out;
}

/**
 * The whole board with one group's rows put in drop order, in the places they
 * already take; every other row stays exactly where it is. Returns the same
 * array when nothing moves.
 */
export function reorderGroup(allRows, groupIds, { keyFn = boardRowKey, idOf = (r) => r.order_line_id } = {}) {
  const ids = new Set([...(groupIds || [])].map(String));
  const positions = [];
  const members = [];
  (allRows || []).forEach((r, i) => {
    if (ids.has(String(idOf(r)))) { positions.push(i); members.push(r); }
  });
  const sorted = sortByDropOrder(members, keyFn);
  if (sorted.every((r, i) => r === members[i])) return allRows;
  const next = allRows.slice();
  positions.forEach((pos, k) => { next[pos] = sorted[k]; });
  return next;
}
